// Shared workout timing components — extracted verbatim from
// SwimmingWorkout.js so the lifting runner can reuse the rest-capture UI
// without forking it. Zero behavior change to swim: the only addition is the
// repLabel/setLabel props on RestClocksDisplay, which default to the exact
// strings the swim screen always rendered.
//
// Both display components own their 100ms tick so ticking never re-renders
// the parent tree. The parent owns every piece of state that changes on real
// events; these render it.

import { useState, useEffect } from 'react';

// ── StopwatchDisplay ──────────────────────────────────────────────────────────
// Owns its own 100ms tick so ticking never re-renders the parent tree.
// Parent holds stopwatchStartTime + stopwatchRunning + frozenStopwatchTime;
// this component renders only ~1 div on each tick.
export function StopwatchDisplay({ startTime, running, frozenTime }) {
  const [elapsed, setElapsed] = useState(frozenTime ?? 0);

  useEffect(() => {
    if (!running || !startTime) {
      setElapsed(frozenTime ?? 0);
      return;
    }
    const id = setInterval(() => setElapsed((Date.now() - startTime) / 1000), 100);
    return () => clearInterval(id);
  }, [running, startTime, frozenTime]);

  if (!running && !frozenTime) return null;
  return (
    <div className="SwimWorkout-poolside-stopwatch-time">
      {formatStopwatch(elapsed)}
    </div>
  );
}

// ── RestClocksDisplay ─────────────────────────────────────────────────────────
// Owns the 100ms rest count-up tick and all rest-clock display logic.
// Parent holds restMode (changes only on real events), frozen values, and targets.
// elapsedRef (parent's useRef) is updated each tick so the parent can read
// the current elapsed when it freezes the clock — up to 100ms stale, display-only.
//
// The 'rep'/'set' mode keys are the component's internal grain vocabulary:
// the finer rest and the coarser rest. Swim renders them as REP/SET; lifting
// relabels them SET/EXER (rest between sets is its fine grain, rest between
// exercises its coarse one) without touching the mechanics.
export function RestClocksDisplay({
  restMode, frozenRepRest, frozenSetRest,
  repTarget, setTarget, adjMode,
  setRepTarget, setSetTarget, setAdjMode,
  elapsedRef,
  repLabel = 'REP', setLabel = 'SET',
}) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!restMode) {
      setElapsed(0);
      elapsedRef.current = 0;
      return;
    }
    elapsedRef.current = 0;
    const now = Date.now();
    const id = setInterval(() => {
      const e = (Date.now() - now) / 1000;
      setElapsed(e);
      elapsedRef.current = e;
    }, 100);
    return () => clearInterval(id);
  }, [restMode]); // eslint-disable-line react-hooks/exhaustive-deps -- elapsedRef is a stable ref, intentionally omitted

  const repIsLive      = restMode === 'rep';
  const setIsLive      = restMode === 'set';
  const repDisplay     = repIsLive ? elapsed : (frozenRepRest ?? 0);
  const setDisplay     = setIsLive ? elapsed : (frozenSetRest ?? 0);
  const liveTarget     = restMode === 'rep' ? repTarget : setTarget;
  const livePct        = restMode !== null && liveTarget > 0 ? elapsed / liveTarget : 0;
  const restIsWarn     = restMode !== null && livePct >= 0.9 && livePct < 1;
  const restIsPast     = restMode !== null && livePct >= 1;
  const restOverage    = restIsPast ? elapsed - liveTarget : 0;
  const liveClockColor = restIsPast ? '#cc0000' : restIsWarn ? '#B8860B' : '#fdf6e3';

  return (
    <div className="SwimWorkout-rest-clocks">
      <div className="SwimWorkout-rest-clock-box">
        {[
          { key: 'rep', label: repLabel, isLive: repIsLive, display: repDisplay, target: repTarget },
          { key: 'set', label: setLabel, isLive: setIsLive, display: setDisplay, target: setTarget },
        ].map(({ key, label, isLive, display, target }) => (
          <div key={key} className="SwimWorkout-rest-clock">
            <div className="SwimWorkout-rest-clock-row">
              <span className="SwimWorkout-rest-clock-label">{label}</span>
              <span
                className={`SwimWorkout-rest-clock-time${isLive ? ' SwimWorkout-rest-clock-time--live' : ''}${restIsWarn && isLive ? ' SwimWorkout-rest-clock-time--warn' : ''}`}
                style={isLive ? { color: liveClockColor } : {}}
              >
                {formatRestMmSs(display)}
                {isLive && restIsPast ? ` (+${formatRestMmSs(restOverage)})` : ''}
              </span>
              <span className="SwimWorkout-rest-clock-target">target {formatRestMmSs(target)}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="SwimWorkout-rest-adj-box">
        <span className="SwimWorkout-rest-adj-which">ADJUSTING: {adjMode === 'rep' ? repLabel : setLabel}</span>
        <button
          className="Fitness-btn SwimWorkout-rest-adj-btn"
          onClick={() => adjMode === 'rep' ? setRepTarget(t => Math.max(5, t + 15)) : setSetTarget(t => Math.max(5, t + 15))}
        >+15</button>
        <button
          className="Fitness-btn SwimWorkout-rest-adj-btn"
          onClick={() => adjMode === 'rep' ? setRepTarget(t => Math.max(5, t - 15)) : setSetTarget(t => Math.max(5, t - 15))}
        >−15</button>
      </div>
    </div>
  );
}

export function formatStopwatch(seconds) {
  const s = Number(seconds);
  const min = Math.floor(s / 60);
  const sec = s % 60;
  if (min > 0) return `${min}:${sec.toFixed(1).padStart(4, '0')}`;
  return `${sec.toFixed(1)}s`;
}

export function formatRestMmSs(sec) {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
